<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { venueNameCityLine } from '@/utils/venueLabel'
import type { RegistrationFormData, FieldAnswer, RegistrationOrganiser } from '@/types/registration'
import type { WizardFieldOption } from '@/types/wizard-forms'
import OrganiserLogo from '@/components/shared/OrganiserLogo.vue'
import RacePriceDisplay from '@/components/shared/RacePriceDisplay.vue'

const props = defineProps<{
  registrationData: RegistrationFormData
  fieldAnswers: Map<number, FieldAnswer>
  selectedOptionsCost: number
  totalAmount: number
  organiser?: RegistrationOrganiser | null
  orgSlug: string
  eventSlug: string
  voucherCode?: string | null
  voucherDiscount?: number
  isVoucherLoading?: boolean
  voucherError?: string | null
  showVoucher?: boolean
}>()

const emit = defineEmits<{
  'apply-voucher': [code: string]
  'remove-voucher': []
}>()

const { t } = useI18n()

// Local state for the voucher input
const isVoucherExpanded = ref(false)
const voucherInput = ref('')

// Already resolved by the backend (race venue, else edition venue, else null).
const startVenueLine = computed<string | null>(() =>
  venueNameCityLine(props.registrationData.race.start_venue),
)

interface SelectedOptionLine {
  label: string
  extraCost: number
}

const selectedOptionsWithCost = computed((): SelectedOptionLine[] => {
  const lines: SelectedOptionLine[] = []
  const form = props.registrationData.wizard_form
  if (!form) return lines

  for (const page of form.pages ?? []) {
    for (const field of page.fields ?? []) {
      const answer = props.fieldAnswers.get(field.id)
      if (!answer) continue

      // Option-based fields (checkbox_group, radio_group, select)
      if (field.options) {
        const selectedIds = answer.selected_options ?? []
        for (const option of field.options as WizardFieldOption[]) {
          if (selectedIds.includes(option.id) && parseFloat(option.extra_cost) > 0) {
            lines.push({
              label: `${field.label}: ${option.label}`,
              extraCost: parseFloat(option.extra_cost),
            })
          }
        }
      }

      // Single checkbox with a field-level extra_cost
      if (field.type === 'checkbox' && field.extra_cost !== null && parseFloat(field.extra_cost) > 0 && answer.value === '1') {
        lines.push({
          label: field.label,
          extraCost: parseFloat(field.extra_cost),
        })
      }
    }
  }

  return lines
})

const basePrice = computed(() =>
  props.registrationData.race.pricing.current_price_cents / 100,
)

// The tier name comes from the backend pricing block.
const baseLabel = computed<string>(() => {
  const tier = props.registrationData.race.pricing.current_tier
  return tier === null
    ? t('registration.summary.base_price_label')
    : t('registration.summary.base_price_label_tier', { tier: tier.name })
})

const hasAppliedVoucher = computed(() => !!props.voucherCode && (props.voucherDiscount ?? 0) > 0)

function toggleVoucherInput(): void {
  isVoucherExpanded.value = !isVoucherExpanded.value
}

function handleApplyVoucher(): void {
  const code = voucherInput.value.trim()
  if (!code) return
  emit('apply-voucher', code)
}

function handleRemoveVoucher(): void {
  voucherInput.value = ''
  isVoucherExpanded.value = false
  emit('remove-voucher')
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(dateTimeStr: string): string {
  return new Date(dateTimeStr).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatCurrency(amount: number): string {
  return `EUR ${amount.toFixed(2)}`
}
</script>

<template>
  <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden">
    <!-- Event logo + name -->
    <div class="p-5 border-b border-slate-100">
      <div class="flex items-center gap-3 mb-3">
        <img
          v-if="registrationData.event.logo_url"
          :src="registrationData.event.logo_url"
          :alt="registrationData.event.name"
          class="w-12 h-12 rounded-lg object-cover shrink-0 border border-slate-100"
        />
        <div
          v-else
          class="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"
        >
          <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <div class="min-w-0">
          <p class="font-semibold text-slate-900 text-sm leading-tight truncate">
            {{ registrationData.event.name }}
          </p>
          <p class="text-xs text-slate-500 mt-0.5">{{ registrationData.edition.name }}</p>
        </div>
      </div>

      <div class="space-y-1">
        <p class="text-sm font-medium text-slate-800">{{ registrationData.race.name }}</p>
        <div class="flex items-center gap-1 text-xs text-slate-500">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>
            {{ formatDateTime(registrationData.race.start_time) }}
          </span>
        </div>
        <div v-if="startVenueLine" class="flex items-center gap-1 text-xs text-slate-500">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{{ startVenueLine }}</span>
        </div>
      </div>
    </div>

    <!-- Cost breakdown -->
    <div class="p-5">
      <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
        {{ t('registration.summary.heading') }}
      </h3>

      <div class="space-y-2">
        <!-- Registration fee -->
        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-600">{{ baseLabel }}</span>
          <span class="font-medium text-slate-900">{{ formatCurrency(basePrice) }}</span>
        </div>
        <RacePriceDisplay :pricing="registrationData.race.pricing" variant="full" notes-only class="items-end text-right" />

        <!-- Selected options with extra cost -->
        <template v-if="selectedOptionsWithCost.length > 0">
          <div
            v-for="(line, i) in selectedOptionsWithCost"
            :key="i"
            class="flex items-center justify-between text-sm"
          >
            <span class="text-slate-500 text-xs">{{ line.label }}</span>
            <span class="text-slate-700 text-xs">+ {{ formatCurrency(line.extraCost) }}</span>
          </div>
        </template>

        <!-- Voucher discount line (shown when a voucher is applied) -->
        <div
          v-if="hasAppliedVoucher"
          class="flex items-center justify-between text-sm"
        >
          <span class="flex items-center gap-1 text-emerald-600 font-medium text-xs">
            {{ t('registration.voucher.discount_line') }}
            <button
              v-if="showVoucher"
              type="button"
              class="ml-0.5 text-red-400 hover:text-red-600 transition-colors"
              :title="t('registration.voucher.remove')"
              :aria-label="t('registration.voucher.remove_label')"
              @click="handleRemoveVoucher"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
          <span class="text-emerald-600 font-medium text-xs">- {{ formatCurrency(voucherDiscount ?? 0) }}</span>
        </div>
      </div>

      <!-- Voucher input section (only shown when showVoucher is true and no voucher applied yet) -->
      <div v-if="showVoucher && !hasAppliedVoucher" class="mt-3">
        <!-- Collapsed state: text link -->
        <button
          v-if="!isVoucherExpanded"
          type="button"
          class="text-xs text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
          :aria-expanded="false"
          @click="toggleVoucherInput"
        >
          {{ t('registration.voucher.have_code') }}
        </button>

        <!-- Expanded state: input + apply button -->
        <div v-else>
          <form
            class="flex items-center gap-2"
            @submit.prevent="handleApplyVoucher"
          >
            <input
              v-model="voucherInput"
              type="text"
              :placeholder="t('registration.voucher.placeholder')"
              :disabled="isVoucherLoading"
              maxlength="50"
              class="flex-1 text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50"
              :aria-describedby="voucherError ? 'voucher-error' : undefined"
            />
            <button
              type="submit"
              :disabled="isVoucherLoading || !voucherInput.trim()"
              class="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-md px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
            >
              <svg
                v-if="isVoucherLoading"
                class="animate-spin h-3.5 w-3.5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {{ t('registration.voucher.apply') }}
            </button>
          </form>

          <!-- Error message -->
          <p
            v-if="voucherError"
            id="voucher-error"
            class="text-xs text-red-500 mt-1"
            role="alert"
          >
            {{ voucherError.includes(' ') ? voucherError : t(voucherError) }}
          </p>
        </div>
      </div>

      <div class="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between">
        <span class="text-sm font-semibold text-slate-800">{{ t('registration.summary.total_label') }}</span>
        <span class="text-base font-bold text-emerald-600">{{ formatCurrency(totalAmount) }}</span>
      </div>
    </div>
  </div>

  <!-- Organiser card -->
  <div
    v-if="organiser"
    class="bg-white rounded-2xl border border-slate-200 overflow-hidden p-5 mt-4"
  >
    <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
      {{ t('event_page.organised_by') }}
    </h3>
    <div class="flex items-center gap-3">
      <OrganiserLogo :src="organiser.logo_url" :alt="organiser.name ?? ''" />
      <p class="font-semibold text-slate-900 text-sm leading-tight">
        {{ organiser.name }}
      </p>
    </div>
    <div class="mt-3 space-y-1.5">
      <a
        v-if="organiser.website"
        :href="organiser.website"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
      >
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        {{ organiser.website }}
      </a>
      <a
        v-if="organiser.email"
        :href="`mailto:${organiser.email}`"
        class="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 transition-colors"
      >
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        {{ organiser.email }}
      </a>
    </div>
  </div>

  <!-- Back to event card -->
  <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden p-5 mt-4">
    <RouterLink
      :to="`/${orgSlug}/${eventSlug}`"
      class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
      {{ t('registration.back_to_event') }}
    </RouterLink>
  </div>
</template>
